let authHeaderGetter: (() => string) | null = null;
let unauthorizedHandler: (() => void) | null = null;
let unauthorizedHandlerFired = false;

export async function addApiAuthHeaderGetter(auth: (() => string) | null) {
	authHeaderGetter = auth;
}

export function getApiAuthHeaderValue(): string | null {
	return authHeaderGetter ? authHeaderGetter():null;
}

export function getApiAuthHeaders(): Record<string, string> {
	const authHeader = getApiAuthHeaderValue();
	return authHeader ? { 'Authorization': authHeader }:{};
}

/**
 * Register a callback invoked once when any API call receives a 401.
 * Fires at most once to prevent redirect loops; reset via `resetUnauthorizedHandler`.
 */
export function onUnauthorizedResponse(handler: () => void) {
	unauthorizedHandler = handler;
	unauthorizedHandlerFired = false;
}

export function resetUnauthorizedHandler() {
	unauthorizedHandlerFired = false;
}

function handlePossible401(response: Response, url: string): void {
	if (response.status !== 401) return;
	if (url.includes('/api/auth/')) return;
	if (unauthorizedHandlerFired || !unauthorizedHandler) return;
	unauthorizedHandlerFired = true;
	unauthorizedHandler();
}

export async function getFromApi<T = unknown>(url: string, options: RequestInit = {}): Promise<T> {
	const headers = getApiAuthHeaders();
	let requestOptions = {
		headers,
		...options
	}
	const response = await fetch(url, {
		credentials: 'include',
		...requestOptions,
	});

	handlePossible401(response, url);

	if (!response.ok) {
		await setErrorFromResponse(response, 'get');
	}

	return response.json();
}

/** Attach a meaningful message or response body to the thrown Error so callers can show it. */
async function setErrorFromResponse(response: Response, _method: string): Promise<never> {
	let errorMessage: string = response.statusText;
	let responseBody: unknown = undefined;
	try {
		const errorData = await response.json();
		responseBody = errorData;
		const rawError = errorData?.error ?? errorData?.message;
		if (typeof rawError === 'string') {
			errorMessage = rawError;
		}
	} catch {
		// If JSON parsing fails, keep statusText
	}
	const err = new Error(errorMessage) as Error & { responseBody?: unknown };
	err.responseBody = responseBody;
	throw err;
}

export async function postToApi<T = unknown>(url: string, data: unknown, options: RequestInit = {}): Promise<T> {
	const headers = getApiAuthHeaders();
	const requestOptions: RequestInit = {
		method: 'POST',
		credentials: 'include',
		body: JSON.stringify(data),
		headers: { ...headers, 'Content-Type': 'application/json' },
		...options
	}
	const response = await fetch(url, requestOptions);

	handlePossible401(response, url);

	if (!response.ok) {
		await setErrorFromResponse(response, 'post');
	}

	return response.json();
}

export async function putToApi<T = unknown>(url: string, data: unknown, options: RequestInit = {}): Promise<T> {
	const headers = getApiAuthHeaders();
	let requestOptions: RequestInit = {
		method: 'PUT',
		credentials: 'include',
		body: JSON.stringify(data),
		headers: { ...headers, 'Content-Type': 'application/json' },
		...options
	}
	const response = await fetch(url, requestOptions);

	handlePossible401(response, url);

	if (!response.ok) {
		const errorText = await response.text();
		throw new Error(`API request failed: ${response.statusText} - ${errorText}`);
	}
	if (response.status===204) {
		return undefined as T;
	}
	return response.json();
}

export async function deleteFromApi(url: string, options: RequestInit = {}): Promise<void> {
	const headers = getApiAuthHeaders();
	let requestOptions: RequestInit = {
		method: 'DELETE',
		credentials: 'include',
		headers: { ...headers },
		...options
	}
	const response = await fetch(url, requestOptions);

	handlePossible401(response, url);

	if (!response.ok) {
		const errorText = await response.text();
		throw new Error(`API request failed: ${response.statusText} - ${errorText}`);
	}
}
